import { ADDON_API_VERSION, type AddonCapabilities, type MaipoAddon, type MaipoAddonHost } from './types';

/**
 * The addon registry.
 *
 * Core never imports an addon; addons call `register` on themselves. The
 * registry validates them, keeps the order they arrived in, and exposes the
 * aggregate capability set the shell needs to describe itself honestly.
 */

type Listener = () => void;

const registered: MaipoAddon[] = [];
const listeners = new Set<Listener>();
const failures: { id: string; reason: string }[] = [];

function validMediaPolicy(value: unknown): boolean {
  if (!value || typeof value !== 'object') return false;
  const policy = value as { accepts?: unknown; effect?: unknown; conversion?: unknown };
  const accepts = ['neutral', 'raster', 'vector', 'text', 'structure'];
  const effects = ['preserve', 'create-raster', 'create-vector', 'rasterize', 'vectorize', 'render-only', 'roundtrip-vector'];
  const conversionEffects = ['rasterize', 'vectorize', 'roundtrip-vector'];
  const effect = String(policy.effect);
  const hasConversion = typeof policy.conversion === 'string' && policy.conversion.trim().length > 0;
  return accepts.includes(String(policy.accepts))
    && effects.includes(effect)
    && (conversionEffects.includes(effect) ? hasConversion : policy.conversion === undefined);
}

function invalid(addon: unknown): string | null {
  if (!addon || typeof addon !== 'object') return 'Addon must be an object.';
  const candidate = addon as Partial<MaipoAddon>;
  if (typeof candidate.id !== 'string' || !/^[a-z0-9]+(?:[.-][a-z0-9]+)*$/i.test(candidate.id)) {
    return 'Addon id must be a dot- or dash-separated identifier.';
  }
  if (typeof candidate.name !== 'string' || !candidate.name.trim()) return 'Addon name is required.';
  if (typeof candidate.version !== 'string' || !candidate.version.trim()) return 'Addon version is required.';
  for (const panel of candidate.panels ?? []) {
    if (typeof panel?.id !== 'string' || typeof panel?.label !== 'string' || typeof panel?.mount !== 'function') {
      return `Panel "${String(panel?.id)}" must declare id, label and mount().`;
    }
    if (panel.media !== undefined && !validMediaPolicy(panel.media)) return `Panel "${panel.id}" has an invalid media policy.`;
  }
  for (const command of candidate.commands ?? []) {
    if (typeof command?.id !== 'string' || typeof command?.label !== 'string' || typeof command?.run !== 'function') {
      return `Command "${String(command?.id)}" must declare id, label and run().`;
    }
    if (command.media !== undefined && !validMediaPolicy(command.media)) return `Command "${command.id}" has an invalid media policy.`;
  }
  const hosts = candidate.capabilities?.network ?? [];
  for (const host of hosts) {
    try {
      const url = new URL(host);
      if (url.protocol !== 'https:' && url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
        return `Network capability "${host}" must be https, or localhost for development.`;
      }
    } catch {
      return `Network capability "${host}" is not a valid origin.`;
    }
  }
  const cloud = candidate.capabilities?.maipoCloud;
  if (cloud !== undefined) {
    if (!cloud || typeof cloud !== 'object') return 'Maipo Cloud capability must be an object.';
    if (cloud.rasterGeneration !== undefined && typeof cloud.rasterGeneration !== 'boolean') {
      return 'Maipo Cloud rasterGeneration capability must be boolean.';
    }
    if (cloud.rasterLab !== undefined && typeof cloud.rasterLab !== 'boolean') {
      return 'Maipo Cloud rasterLab capability must be boolean.';
    }
  }
  return null;
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function registerAddon(addon: MaipoAddon): void {
  const reason = invalid(addon);
  if (reason) {
    failures.push({ id: String((addon as Partial<MaipoAddon>)?.id ?? 'unknown'), reason });
    throw new Error(`Rejected addon: ${reason}`);
  }
  if (registered.some((existing) => existing.id === addon.id)) {
    throw new Error(`Addon "${addon.id}" is already registered.`);
  }
  registered.push(addon);
  emit();
}

export function listAddons(): readonly MaipoAddon[] {
  return [...registered];
}

export function addonFailures(): readonly { id: string; reason: string }[] {
  return [...failures];
}

/**
 * Union of every registered addon's capabilities.
 *
 * `network` being empty is what lets the shell keep claiming "Local only"; the
 * claim is derived from what is actually loaded rather than asserted in markup.
 */
export function aggregateCapabilities(): Required<Pick<AddonCapabilities, 'network'>> & { dataLeavesDevice: string[] } {
  const network = new Set<string>();
  const dataLeavesDevice: string[] = [];
  for (const addon of registered) {
    for (const host of addon.capabilities?.network ?? []) network.add(host);
    const note = addon.capabilities?.dataLeavesDevice;
    if (note) dataLeavesDevice.push(`${addon.name}: ${note}`);
  }
  return { network: [...network], dataLeavesDevice };
}

export function isFullyLocal(): boolean {
  return aggregateCapabilities().network.length === 0;
}

export function subscribeToAddons(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Test-only reset; the shell never calls this. */
export function resetAddonsForTest(): void {
  registered.length = 0;
  failures.length = 0;
  listeners.clear();
}

export function createAddonHost(): MaipoAddonHost {
  return {
    apiVersion: ADDON_API_VERSION,
    register: registerAddon,
    list: listAddons,
  };
}
